import React from 'react'
import classNames from 'classnames'
import classes from './RadioField.scss'

import Radio from '@mui/material/Radio';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Tooltip from '@mui/material/Tooltip';

export type RadioFieldItem<Value> = {
  name: string,
  value: Value,
  image?: string,
  tooltip?: string,
  disabled?: boolean,
  FormControlLabelProps?: Partial<FormControlLabelProps>
}

type RadioFieldProps<Value = string | number> = {
  label?: string,
  items: RadioFieldItem<Value>[],
  imageComponent?: (item: RadioFieldItem<Value>) => React.ReactElement
  imageHeight?: number,
  withImages?: boolean,
  disabled?: boolean,
  FormControlLabelProps?: Partial<FormControlLabelProps>,
} & Pick<RadioGroupProps, 'onChange' | 'value' | 'row'>

const RadioField: React.FC<RadioFieldProps> = function RadioField({
  label,
  onChange,
  imageComponent,
  imageHeight,
  items,
  row = true,
  value,
  withImages = false,
  disabled = false,
  FormControlLabelProps,
}) {
  return items?.length ? (
    <FormControl variant="standard" component="fieldset">
      {label ? <FormLabel component="legend">{label}</FormLabel> : null}
      <RadioGroup
        row={row}
        classes={{ root: classes.radioGroup }}
        onChange={onChange}
        value={value}
      >
        {items?.map(item => (
          <div key={item.value} className={classes.item}>
            <FormControlLabel
              classes={{ root: classes.itemLabel }}
              value={item.value}
              control={(
                <Radio
                  id={`radio_${item.value}`}
                  classes={{ root: classNames({ [classes.hidden]: !item.name }) }}
                  disabled={item.disabled || disabled}
                />
              )}
              label={item.name}
              {...FormControlLabelProps}
              {...item.FormControlLabelProps}
            />
            {withImages && item.image ? (
              <FormControlLabel
                htmlFor={`radio_${item.value}`}
                value={item.value}
                control={imageComponent
                  ? imageComponent(item)
                  : (item.tooltip ?
                      (
                        <Tooltip title={item.tooltip}>
                          <img className={classes.image} src={item.image} alt={item.name} />
                        </Tooltip>
                      )
                      :
                      (
                        <img className={classes.image} src={item.image} alt={item.name} />
                      )
                  )
                }
                label=""
                classes={{
                  root: classNames(classes.imageLabel, {
                    [classes.isActive]: item.value === value
                  })
                }}
                style={
                  imageHeight ? { height: `${imageHeight}px` } : null
                }
                {...FormControlLabelProps}
                {...item.FormControlLabelProps}
              />
            ) : null}
          </div>
        ))}
      </RadioGroup>
    </FormControl>
  ) : null
}

export default RadioField;
